import { COURSE_TYPE } from '@/constants';
import { AppCard } from './AppCard';

import { useLocale } from '@/providers/LocaleProvider';
import IconOpenedBook from '@/resources/images/icon-opened-book.svg';
import CertificateIcon from '@/resources/images/icons-certificate.svg';
import CourseIcon from '@/resources/images/icons-course.svg';
import DiplomaIcon from '@/resources/images/icons-diploma.svg';
import { KLAYO_COLORS } from '@/themes';
import { CourseItem } from '@/types/views/courses.types';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useLocation, useNavigate } from 'react-router-dom';

interface CoursesCardProps {
  item: Partial<CourseItem>;
  isTrainingManager?: boolean;
  /** Show the benchmark match footer (ghost progress / Completed) instead of the module count. */
  showMatchProgress?: boolean;
  /** Current overall match % (solid bar); null when the user has no recorded skills. */
  baseMatchPercentage?: number | null;
  /** Overall match % reached by completing this course (ghost bar target). */
  projectedMatchPercentage?: number;
  isCompleted?: boolean;
  /** Called right before the card navigates — e.g. to close a hosting dialog. */
  onBeforeNavigate?: () => void;
}

export const CoursesCard = ({
  item,
  isTrainingManager,
  showMatchProgress,
  baseMatchPercentage,
  projectedMatchPercentage,
  isCompleted,
  onBeforeNavigate,
}: CoursesCardProps) => {
  const { translate } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  const getCourseTypeIcon = (courseType: string) => {
    switch (courseType) {
      case COURSE_TYPE.DIPLOMA:
        return <DiplomaIcon width={20} height={20} color={KLAYO_COLORS.GreyGR900} />;
      case COURSE_TYPE.CERTIFICATE:
        return <CertificateIcon width={20} height={20} color={KLAYO_COLORS.GreyGR900} />;

      default:
        return <CourseIcon width={20} height={20} color={KLAYO_COLORS.GreyGR900} />;
    }
  };

  return (
    <AppCard
      onClick={() => {
        onBeforeNavigate?.();
        navigate(
          isTrainingManager ? `/settings/learning/${item.id}` : `/my-klayo/courses/${item.id}`,
          {
            preventScrollReset: true,
            // Origin so Course details' Back returns here (and survives the LearnUpon enrolment
            // round-trip via courseFrom), instead of stepping through iframe-polluted history.
            state: { from: location.pathname },
          }
        );
      }}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          position: 'relative',
        }}
      >
        {item.imageUrl ? (
          <LazyLoadImage
            className='klayo_card_image'
            src={item.imageUrl}
            alt={item.title}
            effect='blur'
            width={'100%'}
          />
        ) : (
          <Box
            sx={{
              aspectRatio: '16/10',
              width: '100%',
              borderRadius: '8px 8px 0 0',
              background: `linear-gradient(360deg, ${KLAYO_COLORS.GreyGR200} 0%, ${KLAYO_COLORS.WhiteWH900} 100%)`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <IconOpenedBook width={80} height={80} color={KLAYO_COLORS.GreyGR200} />
          </Box>
        )}
        {item.courseType && (
          <Stack width={'max-content'} position={'absolute'} bottom={'-12px'} left={'24px'}>
            <Chip
              sx={{
                backgroundColor: `${KLAYO_COLORS.WhiteWH900} !important`,
                border: `1px solid ${KLAYO_COLORS.GreyGR900} !important`,

                '& .MuiChip-label': {
                  color: KLAYO_COLORS.GreyGR900,
                  fontWeight: 500,
                  fontSize: '14px',
                },
              }}
              label={item.courseType}
              icon={getCourseTypeIcon(item.courseType)}
            />
          </Stack>
        )}
      </Box>
      <Stack direction='column' gap={3} p={3} flex={1} justifyContent='space-between'>
        <Stack gap={1}>
          <Typography variant='h6' fontSize={18} fontWeight={600}>
            {item.title}
          </Typography>
          {/* <Stack direction='row' spacing={1} alignItems='center' color='#666'>
            <Rating
              value={item.averageRating}
              max={5}
              size='small'
              readOnly
              precision={0.5}
              sx={{
                '& .MuiRating-iconFilled': {
                  color: KLAYO_COLORS.GreyGR900,
                },
                '& .MuiRating-iconHover': {
                  color: KLAYO_COLORS.GreyGR900,
                },
                '& .MuiRating-iconEmpty': {
                  color: KLAYO_COLORS.GreyGR500,
                },
              }}
            />
          </Stack> */}
        </Stack>
        {showMatchProgress ? (
          isCompleted ? (
            <Stack direction={'row'} justifyContent={'flex-end'}>
              <Chip
                label={translate('status.completed')}
                size='small'
                sx={{
                  alignSelf: 'flex-start',
                  fontWeight: 600,
                  fontSize: '12px',
                  backgroundColor: `${KLAYO_COLORS.GreenGR700} !important`,
                  borderColor: `${KLAYO_COLORS.GreenGR700} !important`,
                  '& .MuiChip-label': { color: KLAYO_COLORS.WhiteWH900 },
                }}
              />
            </Stack>
          ) : baseMatchPercentage != null ? (
            <Stack gap={1}>
              {/* Ghost bar: solid = current match, lighter overlay = projected after completion.
                  The unfilled grey track is thinner than the filled green bars. */}
              <Box sx={{ position: 'relative', height: 8 }}>
                {/* Thin grey track, vertically centered behind the bars. */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: KLAYO_COLORS.GreyGR200,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: 8,
                    width: `${projectedMatchPercentage ?? baseMatchPercentage}%`,
                    borderRadius: 4,
                    backgroundColor: KLAYO_COLORS.GreenGR600,
                    opacity: 0.3,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: 8,
                    width: `${baseMatchPercentage}%`,
                    borderRadius: 4,
                    backgroundColor: KLAYO_COLORS.GreenGR600,
                  }}
                />
              </Box>
              <Typography
                variant='body2'
                color={KLAYO_COLORS.GreyGR500}
                sx={{ '& b': { fontWeight: 600, color: KLAYO_COLORS.GreyGR900 } }}
                dangerouslySetInnerHTML={{
                  __html: translate('myKlayo.increasesMatchTo', {
                    percentage: projectedMatchPercentage ?? baseMatchPercentage,
                  }),
                }}
              />
            </Stack>
          ) : null
        ) : (
          <Typography color={KLAYO_COLORS.GreyGR500} variant='body2'>
            {item.isLearningPath
              ? translate('coursesPage.courses', { count: item.numberOfModules })
              : translate('coursesPage.modules', { count: item.numberOfModules })}
          </Typography>
        )}
      </Stack>
    </AppCard>
  );
};
